import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <YourComponent />
 *   </ErrorBoundary>
 *
 * With custom fallback:
 *   <ErrorBoundary fallback={<CustomError />}>
 *     <YourComponent />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Log to PostHog if available
    try {
      const { analytics } = require('../services/analytics');
      analytics.track('error_boundary_caught', {
        error_message: error?.message,
        component_stack: errorInfo?.componentStack?.slice(0, 500),
        component_name: this.props.componentName || 'unknown',
      });
    } catch (e) {
      // Analytics not available, ignore
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Render function fallback (receives error + reset handler)
      if (this.props.renderFallback) {
        return this.props.renderFallback(this.state.error, this.handleReset);
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900 flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-error-500/20 rounded-full mb-6">
              <AlertTriangle className="h-8 w-8 text-error-400" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-2">
              Something went wrong
            </h1>

            <p className="text-gray-400 mb-6">
              An unexpected error occurred. Please try refreshing the page or go back to the home page.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-error-500/10 border border-error-500/20 rounded-lg p-4 mb-6 text-left">
                <p className="text-error-300 text-sm font-mono break-all">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <pre className="text-error-400/70 text-xs mt-2 overflow-auto max-h-40">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>

              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                <Home className="h-4 w-4" />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook for functional components to trigger error boundary
 *
 * Usage:
 *   const throwError = useErrorHandler();
 *
 *   try {
 *     await riskyOperation();
 *   } catch (error) {
 *     throwError(error);
 *   }
 */
export const useErrorHandler = () => {
  const [, setError] = React.useState();

  return React.useCallback((error) => {
    setError(() => {
      throw error;
    });
  }, []);
};

// ========================================
// Specialized Error Boundaries
// ========================================

/**
 * CardErrorBoundary - Compact error fallback for cards
 */
export const CardErrorBoundary = ({ children, title = 'Error loading content' }) => {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-6 glass rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-error-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-error-400" />
            </div>
            <div>
              <h3 className="font-medium text-white">{title}</h3>
              <p className="text-sm text-gray-400">This section couldn't be loaded</p>
            </div>
          </div>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * ChartErrorBoundary - Error fallback for charts/visualizations
 */
export const ChartErrorBoundary = ({ children, height = 200 }) => {
  return (
    <ErrorBoundary
      fallback={
        <div
          className="flex items-center justify-center bg-white/5 rounded-lg"
          style={{ minHeight: height }}
        >
          <div className="text-center p-4">
            <svg
              className="w-10 h-10 text-gray-500 mx-auto mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className="text-sm text-gray-400">Chart failed to load</p>
          </div>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * SectionErrorBoundary - Inline error for page sections
 */
export const SectionErrorBoundary = ({ children, onRetry }) => {
  const [key, setKey] = React.useState(0);

  const handleRetry = () => {
    setKey(prev => prev + 1);
    onRetry?.();
  };

  return (
    <ErrorBoundary
      key={key}
      fallback={
        <div className="py-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-error-500/20 rounded-full mb-4">
            <AlertTriangle className="w-6 h-6 text-error-400" />
          </div>
          <p className="text-gray-400 mb-4">Failed to load this section</p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 text-sm text-portfolio-purple hover:underline"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * Classify error for user-friendly messaging
 */
const classifyError = (error) => {
  const msg = (error?.message || '').toLowerCase();
  if (msg.includes('timeout') || msg.includes('timed out') || msg.includes('llm') || msg.includes('analysis')) {
    return 'llm_timeout';
  }
  if (msg.includes('network') || msg.includes('failed to fetch') || msg.includes('load failed')) {
    return 'network';
  }
  return 'generic';
};

/**
 * RouteErrorBoundary - Per-route error boundary with smart fallback messaging.
 * Handles LLM timeouts, API failures, and generic errors with retry support.
 */
export const RouteErrorBoundary = ({ children }) => {
  const [key, setKey] = React.useState(0);

  return (
    <ErrorBoundary
      key={key}
      renderFallback={(error, reset) => {
        const type = classifyError(error);
        const isLLMTimeout = type === 'llm_timeout';

        const title = isLLMTimeout
          ? 'Analysis taking longer than expected'
          : 'This page couldn\'t be loaded';

        const message = isLLMTimeout
          ? 'The AI analysis is still running in the background. Check back shortly or retry.'
          : 'An unexpected error occurred. Please try again or go back to the home page.';

        const handleRetry = () => {
          reset();
          setKey(k => k + 1);
        };

        return (
          <div className="min-h-[60vh] flex items-center justify-center p-4">
            <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-error-500/20 rounded-full mb-6">
                <AlertTriangle className="h-8 w-8 text-error-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{title}</h2>
              <p className="text-gray-400 mb-6">{message}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>
                <button
                  onClick={() => { window.location.href = '/'; }}
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  <Home className="h-4 w-4" />
                  Go Home
                </button>
              </div>
            </div>
          </div>
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * withErrorBoundary HOC - Wrap any component with error boundary
 *
 * Usage:
 *   const SafeComponent = withErrorBoundary(RiskyComponent);
 */
export const withErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
  const WithErrorBoundary = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `WithErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithErrorBoundary;
};

export default ErrorBoundary;
